'use client';
import React from 'react';
import { useGed } from '@/lib/store';
import { card, StatePill, Skeleton, SkeletonTiles, EmptyState, enChargement } from '@/components/ui';
import { H1 } from './commun';

// Vue auditeur — panorama en lecture seule.
/* ------------------------------ Vue auditeur ------------------------------ */
export function VueAuditeur() {
  const { docs, ncs, actions, audits, openDoc, go, goFiltre, setFilter, loading, booted } = useGed();
  /* ⚠️ LES QUATRE COLLECTIONS, pas seulement `docs` (BLOQ-03). Cet écran est
     celui qu'on montre à un évaluateur externe : afficher « 0 NC ouverte »
     parce que le registre n'est pas encore arrivé serait, littéralement, une
     déclaration fausse faite à un auditeur. */
  const attend = enChargement(booted, loading, docs.length, ncs.length, actions.length, audits.length);
  const vig = docs.filter((d) => d.statut === 'envigueur');

  /** Un compteur désigne un sous-ensemble : il doit y mener, filtre déjà posé.
   *  Même motif que les tuiles du registre des NC et du tableau de bord. */
  const versCatalogue = (statut: string) => {
    setFilter('fQuery', ''); setFilter('fType', ''); setFilter('fDisc', '');
    setFilter('fStatut', statut);
    go('list');
  };
  const compteurs = [
    { v: vig.length, l: 'Docs en vigueur', aide: 'Voir le catalogue des documents en vigueur', ouvrir: () => versCatalogue('envigueur') },
    { v: ncs.filter((n) => n.statut !== 'cloturee').length, l: 'NC ouvertes', aide: 'Voir le registre des non-conformités ouvertes', ouvrir: () => goFiltre('nc', 'ouvertes') },
    { v: ncs.filter((n) => n.statut === 'cloturee').length, l: 'NC clôturées', aide: 'Voir le registre des non-conformités clôturées', ouvrir: () => goFiltre('nc', 'cloturees') },
    { v: actions.filter((a) => a.statut === 'verifiee').length, l: 'Actions vérifiées', aide: 'Voir le plan d’actions dont l’efficacité est vérifiée', ouvrir: () => goFiltre('actions', 'verifiees') },
    { v: audits.filter((a) => a.statut === 'cloture').length, l: 'Audits clôturés', aide: 'Voir le programme des audits clôturés', ouvrir: () => goFiltre('audits', 'clotures') },
  ];
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <H1 eyebrow="Accès lecture seule" title="Vue auditeur" />
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, maxWidth: '72ch' }}>
        Panorama en lecture seule pour l’auditeur externe : documents en vigueur, traitement des non-conformités et plan d’actions.
        L’auditeur accède à l’historique complet et au journal signé de chaque document, sans rien pouvoir modifier.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
        {attend ? <SkeletonTiles n={5} /> : compteurs.map((k) => (
          <button key={k.l} onClick={k.ouvrir} title={k.aide} className="ged-btn"
            style={{ ...card, padding: '14px 16px', textAlign: 'start', cursor: 'pointer', font: 'inherit', fontFamily: 'var(--font-sans)' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 24, fontWeight: 600, color: 'var(--navy-900)', lineHeight: 1 }}>{k.v}</div>
            <div style={{ fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 6 }}>{k.l}</div>
          </button>
        ))}
      </div>
      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Fonds documentaire en vigueur</div>
        {attend ? <Skeleton rows={4} height={41} /> : vig.length === 0 ? (
          <EmptyState titre="Aucun document en vigueur." indice="Le fonds documentaire se remplit à mesure que les documents sont approuvés et diffusés." />
        ) : vig.map((d) => (
          <button key={d.documentId} onClick={() => openDoc(d.code, 'journal')} className="ged-row" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start', background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '10px 16px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{d.code}</span>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)' }}>{d.titre}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>v{d.versionCourante}</span>
            <StatePill statut={d.statut} />
          </button>
        ))}
      </div>
    </div>
  );
}
